import { PackagingApiAdapter } from '../adapters/PackagingApiAdapter';
import { CreatePackagingRequest, PackagingFilters, PackagingItem, PackagingPage } from '../models/Packaging';

export class PackagingService {
  constructor(private apiAdapter: PackagingApiAdapter) {}

  async getPackagingPaginated(filters: PackagingFilters = {}): Promise<PackagingPage> {
    const { searchTerm, limit } = filters;

    if (limit && limit > 100) {
      throw new Error('El límite no puede ser mayor a 100');
    }

    if (searchTerm && searchTerm.length < 2) {
      throw new Error('El término de búsqueda debe tener al menos 2 caracteres');
    }

    return this.apiAdapter.getPackagingPaginated(filters);
  }

  async getAllPackaging(filters: PackagingFilters = {}): Promise<PackagingItem[]> {
    return this.apiAdapter.getPackaging(filters);
  }

  async createPackaging(data: CreatePackagingRequest): Promise<PackagingItem> {
    if (!data.name || data.name.trim().length < 2) {
      throw new Error('El nombre del material debe tener al menos 2 caracteres');
    }

    if (!data.sku || data.sku.trim().length === 0) {
      throw new Error('El SKU es obligatorio');
    }

    if (!data.category) {
      throw new Error('La categoría es obligatoria');
    }

    if (data.category === 'own_box') {
      if (!data.rates || data.rates.length === 0) {
        throw new Error('Debes indicar el precio en cada tarifa activa');
      }
      for (const entry of data.rates) {
        if (!entry.rate_id || entry.rate_id <= 0) {
          throw new Error('ID de tarifa inválido');
        }
        if (entry.price === undefined || entry.price === null || entry.price < 0) {
          throw new Error('El precio por tarifa debe ser 0 o mayor');
        }
      }
    } else if (data.pvp !== undefined && data.pvp < 0) {
      throw new Error('El PVP no puede ser negativo');
    }

    const packagingData: CreatePackagingRequest = {
      name: data.name.trim(),
      sku: data.sku.trim(),
      category: data.category,
      ...(data.team_id ? { team_id: data.team_id } : {}),
      ...(data.category !== 'own_box' && data.pvp !== undefined ? { pvp: data.pvp } : {}),
      ...(data.category === 'own_box' && data.rates ? { rates: data.rates } : {}),
      ...(data.packaging_box_group_id ? { packaging_box_group_id: data.packaging_box_group_id } : {}),
      ...(data.image_hash ? { image_hash: data.image_hash } : {}),
      ...(data.folded_dimensions ? { folded_dimensions: data.folded_dimensions } : {}),
      ...(data.assembled_dimensions ? { assembled_dimensions: data.assembled_dimensions } : {}),
      ...(data.dimension_unit ? { dimension_unit: data.dimension_unit } : {}),
    };

    return this.apiAdapter.createPackaging(packagingData);
  }

  async updatePackaging(id: number, data: CreatePackagingRequest): Promise<PackagingItem> {
    if (!id || id <= 0) {
      throw new Error('ID inválido');
    }

    if (!data.name || data.name.trim().length < 2) {
      throw new Error('El nombre del material debe tener al menos 2 caracteres');
    }

    if (!data.sku || data.sku.trim().length === 0) {
      throw new Error('El SKU es obligatorio');
    }

    if (!data.category) {
      throw new Error('La categoría es obligatoria');
    }

    if (data.pvp !== undefined && data.pvp < 0) {
      throw new Error('El PVP no puede ser negativo');
    }

    const packagingData: CreatePackagingRequest = {
      name: data.name.trim(),
      sku: data.sku.trim(),
      category: data.category,
      ...(data.team_id !== undefined ? { team_id: data.team_id } : {}),
      ...(data.pvp !== undefined ? { pvp: data.pvp } : {}),
      ...(data.packaging_box_group_id ? { packaging_box_group_id: data.packaging_box_group_id } : {}),
      ...(data.image_hash ? { image_hash: data.image_hash } : {}),
      ...(data.folded_dimensions ? { folded_dimensions: data.folded_dimensions } : {}),
      ...(data.assembled_dimensions ? { assembled_dimensions: data.assembled_dimensions } : {}),
      ...(data.dimension_unit ? { dimension_unit: data.dimension_unit } : {}),
    };

    return this.apiAdapter.updatePackaging(id, packagingData);
  }

  async deletePackaging(id: number): Promise<void> {
    if (!id || id <= 0) {
      throw new Error('ID inválido');
    }
    return this.apiAdapter.deletePackaging(id);
  }
}
